import NumberInput from '@lyra/core/components/Input/NumberInput'
import Section from '@lyra/core/components/Section'
import VideoText from '@lyra/core/components/VideoText'
import { Ticker } from '@lyra/web/constants/instruments'
import {
  OrderQuote,
  OrderQuoteParams,
  SetOrderQuoteParamsForInstrument,
} from '@lyra/web/constants/order'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { roundDownToStep } from '@lyra/web/utils/number'
import { getOrderMinAmount, getValidOrderAmount } from '@lyra/web/utils/order'
import { useCallback, useState } from 'react'
import { Slider } from 'tamagui'

type Props = {
  quote?: OrderQuote | null
  ticker: Ticker
  quoteParams: OrderQuoteParams
  onChangeQuoteParams: SetOrderQuoteParamsForInstrument
}

const TradeFormAmountSlider = ({ ticker, quote, quoteParams, onChangeQuoteParams }: Props) => {
  const amountStep = +ticker.amount_step

  const minAmount = getOrderMinAmount(quoteParams, ticker)

  const { subaccount } = useSubaccount()

  const amount = roundDownToStep(Math.max(+quoteParams.amount, minAmount), amountStep)
  const handleSetAmount = useCallback(
    (newAmount: number) => {
      onChangeQuoteParams((params) => ({
        ...params,
        amount: getValidOrderAmount(newAmount, quoteParams, ticker).toString(),
      }))
    },
    [onChangeQuoteParams, quoteParams, ticker]
  )

  const maxAmount =
    // allow 97% of max amount
    quote && quote.max_amount ? roundDownToStep(+quote.max_amount * 0.97, amountStep) : 0
  const maxAmountUtilRaw = Math.max(0, Math.min(maxAmount !== 0 ? amount / maxAmount : 0, 1))
  const maxAmountUtil = Math.round(maxAmountUtilRaw * 100) / 100

  const handleSetAmountUtil = useCallback(
    (amountUtil: number) => {
      const newAmount = amountUtil * maxAmount
      onChangeQuoteParams((params) => ({
        ...params,
        amount: getValidOrderAmount(newAmount, quoteParams, ticker).toString(),
      }))
    },
    [onChangeQuoteParams, maxAmount, quoteParams, ticker]
  )

  const [demoAmountUtil, setDemoAmountUtil] = useState(0)

  const isDemo = !subaccount

  if (isDemo) {
    return (
      <Section.XStack isCompact alignItems="center">
        <Slider
          height={12}
          flex={1}
          cursor="pointer"
          value={[demoAmountUtil]}
          onValueChange={(vals) => setDemoAmountUtil(vals[0])}
          min={0}
          max={100}
          step={0.1}
        >
          <Slider.Track height={4} borderRadius="$1" backgroundColor="$hairline">
            <Slider.TrackActive />
          </Slider.Track>
          <Slider.Thumb
            backgroundColor="$primaryText"
            size={0}
            height={12}
            width={6}
            index={0}
            borderRadius="$1"
            transform="translateY(-6px)"
            borderWidth={0}
            alignItems="center"
            justifyContent="center"
          />
        </Slider>
        <NumberInput
          width={85}
          value={demoAmountUtil}
          renderAsPlaceholder={!demoAmountUtil}
          onChangeValue={(amountUtil100) => setDemoAmountUtil(amountUtil100)}
          formatValue={(amountUtil100) => (Math.round(amountUtil100 * 100) / 100).toFixed(1)}
          rightContent={<VideoText color="secondary">%</VideoText>}
        />
      </Section.XStack>
    )
  }

  return (
    <Section.XStack isCompact alignItems="center">
      <Slider
        height={12}
        flex={1}
        cursor={quote ? 'pointer' : undefined}
        value={[Math.min(amount, maxAmount)]}
        onValueChange={quote ? (vals) => handleSetAmount(vals[0]) : undefined}
        min={minAmount}
        step={amountStep}
        max={maxAmount}
      >
        <Slider.Track height={4} borderRadius="$1" backgroundColor="$hairline">
          <Slider.TrackActive />
        </Slider.Track>
        {quote ? (
          <Slider.Thumb
            backgroundColor="$primaryText"
            size={0}
            height={12}
            width={6}
            index={0}
            borderRadius="$1"
            transform="translateY(-6px)"
            borderWidth={0}
            alignItems="center"
            justifyContent="center"
          />
        ) : null}
      </Slider>
      <NumberInput
        width={85}
        value={maxAmountUtil * 100}
        renderAsPlaceholder={amount <= minAmount}
        onChangeValue={(amountUtil100) => handleSetAmountUtil(amountUtil100 / 100)}
        formatValue={(amountUtil100) => (Math.round(amountUtil100 * 100) / 100).toFixed(1)}
        rightContent={<VideoText color="secondary">%</VideoText>}
        isDisabled={!quote}
      />
    </Section.XStack>
  )
}

export default TradeFormAmountSlider
